import {
  createTaskEditorActionComponent,
  createTaskEditorBlock,
  createTaskEditorButtonOption,
  createTaskEditorDescription,
  createTaskEditorLabelPair,
  createTaskEditorTextInputField,
  getTaskEditorBranchRunSpan,
  getTaskEditorBranchSegmentSpan,
  TaskEditorActionComponent,
  TaskEditorActionComponentType,
  TaskEditorBlock,
  TaskEditorBlockType,
  TaskEditorButtonOption,
  TaskEditorDescription,
  TaskEditorDescriptionType,
  TaskEditorLabelPair,
  TaskEditorReferenceType,
  TaskEditorTextInputField,
} from "./taskEditorUtils";

// ─── 헬퍼 ─────────────────────────────────────────────────────────────────

function replaceBlock(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  updater: (block: TaskEditorBlock) => TaskEditorBlock
): TaskEditorBlock[] {
  if (blockIndex < 0 || blockIndex >= blocks.length) return blocks;
  const target = blocks[blockIndex];
  const next = updater(target);
  if (next === target) return blocks;
  const out = [...blocks];
  out[blockIndex] = next;
  return out;
}

function replaceDescription(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  updater: (description: TaskEditorDescription) => TaskEditorDescription
): TaskEditorBlock[] {
  return replaceBlock(blocks, blockIndex, (block) => {
    const next = updater(block.description);
    if (next === block.description) return block;
    return { ...block, description: next };
  });
}

function replaceAction(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  updater: (action: TaskEditorActionComponent) => TaskEditorActionComponent
): TaskEditorBlock[] {
  return replaceBlock(blocks, blockIndex, (block) => {
    if (!block.action) return block;
    const next = updater(block.action);
    if (next === block.action) return block;
    return { ...block, action: next };
  });
}

// ─── Block ────────────────────────────────────────────────────────────────

export function applyAddBlock(
  blocks: TaskEditorBlock[],
  insertionIndex?: number,
  type: TaskEditorBlockType = "body"
): TaskEditorBlock[] {
  const next = [...blocks];
  const idx =
    typeof insertionIndex === "number"
      ? Math.max(0, Math.min(insertionIndex, blocks.length))
      : blocks.length;
  next.splice(idx, 0, createTaskEditorBlock(type));
  return next;
}

// allowEmpty: 분기 세그먼트 스코프 편집에서는 마지막 블록을 지워 빈 세그먼트
// ([]) 가 될 수 있다. 루트는 기존대로 빈 블록 1 개로 리셋.
export function applyRemoveBlock(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  allowEmpty = false
): TaskEditorBlock[] {
  if (blockIndex < 0 || blockIndex >= blocks.length) return blocks;
  // 분기 블록 삭제 = 세그먼트 run 도 함께 삭제 (고아 태그 방지)
  const target = blocks[blockIndex];
  const run =
    target.action?.type === "button_branch"
      ? getTaskEditorBranchRunSpan(blocks, blockIndex)
      : null;
  const removeEnd = run ? run.end : blockIndex + 1;
  const next = [...blocks];
  next.splice(blockIndex, removeEnd - blockIndex);
  if (next.length === 0) {
    // 마지막 블록 제거 시 빈 블록 1 개로 리셋
    return allowEmpty ? [] : [createTaskEditorBlock()];
  }
  return next;
}

// ─── Block type ───────────────────────────────────────────────────────────

export function applyBlockTypeChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  type: TaskEditorBlockType
): TaskEditorBlock[] {
  return replaceBlock(blocks, blockIndex, (block) => {
    if (block.type === type) return block;
    return { ...block, type };
  });
}

// ─── Action (add / remove / type change) ─────────────────────────────────

export function applyAddAction(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  type: TaskEditorActionComponentType
): TaskEditorBlock[] {
  return replaceBlock(blocks, blockIndex, (block) => {
    // 이미 있으면 거부 (shape 으로 max-1 강제)
    if (block.action) return block;
    return { ...block, action: createTaskEditorActionComponent(type) };
  });
}

export function applyRemoveActionComponent(
  blocks: TaskEditorBlock[],
  blockIndex: number
): TaskEditorBlock[] {
  return replaceBlock(blocks, blockIndex, (block) => {
    if (!block.action) return block;
    return { ...block, action: null };
  });
}

// type 변경 시 content 는 보존, 나머지 필드는 새 type 의 기본값.
export function applyDescriptionTypeChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  nextType: TaskEditorDescriptionType
): TaskEditorBlock[] {
  return replaceDescription(blocks, blockIndex, (description) => {
    if (description.type === nextType) return description;
    const fresh = createTaskEditorDescription(nextType);
    return { ...fresh, content: description.content };
  });
}

export function applyActionComponentTypeChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  nextType: TaskEditorActionComponentType
): TaskEditorBlock[] {
  const target = blocks[blockIndex];
  if (!target?.action || target.action.type === nextType) return blocks;
  // 분기 → 다른 타입: 세그먼트 블록은 untag 해 메인 흐름으로 흡수 (본문 보존)
  let working = blocks;
  if (target.action.type === "button_branch") {
    const run = getTaskEditorBranchRunSpan(blocks, blockIndex);
    if (run && run.end > run.start + 1) {
      working = [...blocks];
      for (let i = run.start + 1; i < run.end; i += 1) {
        const { branch_option: _tag, ...rest } = working[i];
        working[i] = rest;
      }
    }
  }
  return replaceBlock(working, blockIndex, (block) => {
    if (!block.action) return block;
    const replaced = migrateActionComponentToType(block.action, nextType);
    return { ...block, action: replaced };
  });
}

// 같은 button category 안에서 type 만 바꾸는 경우 옵션 보존.
function migrateActionComponentToType(
  current: TaskEditorActionComponent,
  nextType: TaskEditorActionComponentType
): TaskEditorActionComponent {
  const fresh = createTaskEditorActionComponent(nextType);
  const isCurrentButton =
    current.type === "button_default" ||
    current.type === "button_quiz" ||
    current.type === "button_branch";
  const isNextButton =
    nextType === "button_default" ||
    nextType === "button_quiz" ||
    nextType === "button_branch";

  if (isCurrentButton && isNextButton) {
    const options =
      "options" in current && Array.isArray(current.options)
        ? current.options
        : [];
    const fallbackOptions = [
      createTaskEditorButtonOption(),
      createTaskEditorButtonOption(),
    ];
    if (nextType === "button_default") {
      return {
        type: "button_default",
        allow_multiple_selection:
          current.type === "button_default"
            ? current.allow_multiple_selection
            : true,
        options: options.length > 0 ? options : fallbackOptions,
      };
    }
    if (nextType === "button_quiz") {
      return {
        type: "button_quiz",
        options: options.length > 0 ? options : fallbackOptions,
      };
    }
    if (nextType === "button_branch") {
      return {
        type: "button_branch",
        options: options.length > 0 ? options : fallbackOptions,
      };
    }
  }
  return fresh;
}

// ─── description 필드 ────────────────────────────────────────────────────

export function applyDescriptionContentChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  content: string
): TaskEditorBlock[] {
  return replaceDescription(blocks, blockIndex, (d) => {
    if (d.content === content) return d;
    return { ...d, content };
  });
}

export function applyDescriptionImageUrlChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  imageUrl: string
): TaskEditorBlock[] {
  return replaceDescription(blocks, blockIndex, (d) => {
    if (d.type !== "image") return d;
    if (d.image_url === imageUrl) return d;
    return { ...d, image_url: imageUrl };
  });
}

// ─── label pairs (description) ───────────────────────────────────────────

function withLabelPairs(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  updater: (pairs: TaskEditorLabelPair[]) => TaskEditorLabelPair[]
): TaskEditorBlock[] {
  return replaceDescription(blocks, blockIndex, (d) => {
    if (d.type !== "label") return d;
    const nextPairs = updater(d.pairs);
    if (nextPairs === d.pairs) return d;
    return { ...d, pairs: nextPairs };
  });
}

export function applyAddLabelPair(
  blocks: TaskEditorBlock[],
  blockIndex: number
): TaskEditorBlock[] {
  return withLabelPairs(blocks, blockIndex, (pairs) => [
    ...pairs,
    createTaskEditorLabelPair(),
  ]);
}

export function applyRemoveLabelPair(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  pairIndex: number
): TaskEditorBlock[] {
  return withLabelPairs(blocks, blockIndex, (pairs) => {
    if (pairs.length <= 1) return pairs;
    if (pairIndex < 0 || pairIndex >= pairs.length) return pairs;
    return pairs.filter((_, i) => i !== pairIndex);
  });
}

export function applyLabelPairChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  pairIndex: number,
  key: "label" | "text",
  value: string
): TaskEditorBlock[] {
  return withLabelPairs(blocks, blockIndex, (pairs) => {
    if (pairIndex < 0 || pairIndex >= pairs.length) return pairs;
    if (pairs[pairIndex][key] === value) return pairs;
    const out = [...pairs];
    out[pairIndex] = { ...out[pairIndex], [key]: value };
    return out;
  });
}

export function applyReferenceTypeChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  referenceType: TaskEditorReferenceType
): TaskEditorBlock[] {
  return replaceDescription(blocks, blockIndex, (d) => {
    if (d.type !== "reference") return d;
    if (d.reference_type === referenceType) return d;
    return referenceType === "coach_preset"
      ? {
          type: "reference",
          content: d.content,
          reference_type: "coach_preset",
          preset_text: "",
        }
      : {
          type: "reference",
          content: d.content,
          reference_type: "automatic_thought",
        };
  });
}

export function applyReferencePresetTextChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  text: string
): TaskEditorBlock[] {
  return replaceDescription(blocks, blockIndex, (d) => {
    if (d.type !== "reference" || d.reference_type !== "coach_preset") {
      return d;
    }
    if (d.preset_text === text) return d;
    return { ...d, preset_text: text };
  });
}

// ─── text_input fields (action) ──────────────────────────────────────────

function withTextInput(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  updater: (fields: TaskEditorTextInputField[]) => TaskEditorTextInputField[]
): TaskEditorBlock[] {
  return replaceAction(blocks, blockIndex, (a) => {
    if (a.type !== "text_input") return a;
    const nextFields = updater(a.fields);
    if (nextFields === a.fields) return a;
    return { ...a, fields: nextFields };
  });
}

export function applyAddTextInputField(
  blocks: TaskEditorBlock[],
  blockIndex: number
): TaskEditorBlock[] {
  return withTextInput(blocks, blockIndex, (fields) => [
    ...fields,
    createTaskEditorTextInputField(),
  ]);
}

export function applyRemoveTextInputField(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  fieldIndex: number
): TaskEditorBlock[] {
  return withTextInput(blocks, blockIndex, (fields) => {
    if (fields.length <= 1) return fields;
    if (fieldIndex < 0 || fieldIndex >= fields.length) return fields;
    return fields.filter((_, i) => i !== fieldIndex);
  });
}

export function applyTextInputFieldChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  fieldIndex: number,
  key: "label" | "placeholder" | "help_text",
  value: string
): TaskEditorBlock[] {
  return withTextInput(blocks, blockIndex, (fields) => {
    if (fieldIndex < 0 || fieldIndex >= fields.length) return fields;
    const target = fields[fieldIndex];
    const currentValue = target[key];
    const nextField: TaskEditorTextInputField = { ...target };
    if (value === "") {
      delete nextField[key];
    } else {
      nextField[key] = value;
    }
    if (currentValue === nextField[key]) return fields;
    const out = [...fields];
    out[fieldIndex] = nextField;
    return out;
  });
}

// optional 토글 — 기본 필수. true 일 때만 저장하고 false 면 키 삭제
// (button_quiz 의 is_correct 와 동일 컨벤션).
export function applyTextInputFieldOptionalToggle(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  fieldIndex: number,
  optional: boolean
): TaskEditorBlock[] {
  return withTextInput(blocks, blockIndex, (fields) => {
    if (fieldIndex < 0 || fieldIndex >= fields.length) return fields;
    const target = fields[fieldIndex];
    if ((target.optional ?? false) === optional) return fields;
    const nextField: TaskEditorTextInputField = { ...target };
    if (optional) {
      nextField.optional = true;
    } else {
      delete nextField.optional;
    }
    const out = [...fields];
    out[fieldIndex] = nextField;
    return out;
  });
}

// ─── button options (action) ─────────────────────────────────────────────

type ButtonAction = Extract<
  TaskEditorActionComponent,
  { type: "button_default" | "button_quiz" | "button_branch" }
>;

function isButtonAction(a: TaskEditorActionComponent): a is ButtonAction {
  return (
    a.type === "button_default" ||
    a.type === "button_quiz" ||
    a.type === "button_branch"
  );
}

function withButtonOptions(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  updater: (options: TaskEditorButtonOption[]) => TaskEditorButtonOption[]
): TaskEditorBlock[] {
  return replaceAction(blocks, blockIndex, (a) => {
    if (!isButtonAction(a)) return a;
    const nextOptions = updater(a.options);
    if (nextOptions === a.options) return a;
    return { ...a, options: nextOptions } as ButtonAction;
  });
}

export function applyAddButtonOption(
  blocks: TaskEditorBlock[],
  blockIndex: number
): TaskEditorBlock[] {
  return withButtonOptions(blocks, blockIndex, (options) => [
    ...options,
    createTaskEditorButtonOption(),
  ]);
}

export function applyRemoveButtonOption(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  optionIndex: number
): TaskEditorBlock[] {
  const target = blocks[blockIndex];
  // 분기 옵션 삭제: 그 옵션 세그먼트 블록 삭제 + 더 큰 태그는 -1 재매핑
  if (target?.action?.type === "button_branch") {
    const action = target.action;
    if (optionIndex < 0 || optionIndex >= action.options.length) return blocks;
    const remaining = action.options.filter((_, i) => i !== optionIndex);
    const nextOptions =
      remaining.length > 0
        ? remaining
        : [createTaskEditorButtonOption(), createTaskEditorButtonOption()];
    const run = getTaskEditorBranchRunSpan(blocks, blockIndex);
    const runEnd = run ? run.end : blockIndex + 1;
    const next: TaskEditorBlock[] = blocks.slice(0, blockIndex);
    next.push({ ...target, action: { ...action, options: nextOptions } });
    for (let i = blockIndex + 1; i < runEnd; i += 1) {
      const tag = blocks[i].branch_option;
      if (tag === optionIndex) continue; // 삭제된 옵션의 세그먼트 블록은 함께 삭제
      if (typeof tag === "number" && tag > optionIndex) {
        next.push({ ...blocks[i], branch_option: tag - 1 });
      } else {
        next.push(blocks[i]);
      }
    }
    next.push(...blocks.slice(runEnd));
    return next;
  }
  return withButtonOptions(blocks, blockIndex, (options) => {
    if (optionIndex < 0 || optionIndex >= options.length) return options;
    const next = options.filter((_, i) => i !== optionIndex);
    return next.length > 0
      ? next
      : [createTaskEditorButtonOption(), createTaskEditorButtonOption()];
  });
}

export function applyButtonOptionTextChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  optionIndex: number,
  text: string
): TaskEditorBlock[] {
  return withButtonOptions(blocks, blockIndex, (options) => {
    if (optionIndex < 0 || optionIndex >= options.length) return options;
    if (options[optionIndex].text === text) return options;
    const out = [...options];
    out[optionIndex] = { ...out[optionIndex], text };
    return out;
  });
}

export function applyButtonQuizOptionCorrectToggle(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  optionIndex: number,
  isCorrect: boolean
): TaskEditorBlock[] {
  return replaceAction(blocks, blockIndex, (a) => {
    if (a.type !== "button_quiz") return a;
    if (optionIndex < 0 || optionIndex >= a.options.length) return a;
    const current = a.options[optionIndex];
    if ((current.is_correct ?? false) === isCorrect) return a;
    const nextOption: TaskEditorButtonOption = { ...current };
    if (isCorrect) {
      nextOption.is_correct = true;
    } else {
      delete nextOption.is_correct;
    }
    const next = [...a.options];
    next[optionIndex] = nextOption;
    return { ...a, options: next };
  });
}

// 분기 세그먼트 통째 교체 — 드릴다운 편집의 lift 프리미티브. 해당 옵션의
// [start, end) 구간을 segmentBlocks 로 splice 하며, 어떤 편집을 거쳤든 각
// 블록에 branch_option 을 강제 재태깅해 태그 일관성을 보장한다.
export function applyBranchSegmentBlocksChange(
  blocks: TaskEditorBlock[],
  branchBlockIndex: number,
  optionIndex: number,
  segmentBlocks: TaskEditorBlock[]
): TaskEditorBlock[] {
  const span = getTaskEditorBranchSegmentSpan(
    blocks,
    branchBlockIndex,
    optionIndex
  );
  if (!span) return blocks;
  const retagged = segmentBlocks.map((block) =>
    block.branch_option === optionIndex
      ? block
      : { ...block, branch_option: optionIndex }
  );
  const currentLength = span.end - span.start;
  if (
    retagged.length === currentLength &&
    retagged.every((block, i) => block === blocks[span.start + i])
  ) {
    return blocks;
  }
  const next = [...blocks];
  next.splice(span.start, currentLength, ...retagged);
  return next;
}

export function applyButtonDefaultAllowMultipleChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  allow: boolean
): TaskEditorBlock[] {
  return replaceAction(blocks, blockIndex, (a) => {
    if (a.type !== "button_default") return a;
    if (a.allow_multiple_selection === allow) return a;
    return { ...a, allow_multiple_selection: allow };
  });
}

// ─── audio (action) ───────────────────────────────────────────────────────

// extra_audio_url 은 선택값이라 빈 문자열이면 키를 삭제(is_correct·optional 컨벤션).
// text / artwork / audio_url 은 항상 보존되는 문자열.
export function applyAudioFieldChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  key: "artwork" | "audio_url" | "extra_audio_url",
  value: string
): TaskEditorBlock[] {
  return replaceAction(blocks, blockIndex, (a) => {
    if (a.type !== "audio") return a;
    if (key === "extra_audio_url") {
      if ((a.extra_audio_url ?? "") === value) return a;
      const next = { ...a };
      if (value === "") {
        delete next.extra_audio_url;
      } else {
        next.extra_audio_url = value;
      }
      return next;
    }
    if (a[key] === value) return a;
    return { ...a, [key]: value };
  });
}
